<script setup lang="ts">
import { CalendarDays, ChevronLeft, ChevronRight } from "@lucide/vue";
import {
  onClickOutside,
  useElementBounding,
  useEventListener,
} from "@vueuse/core";
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "../composables/useI18n";
import { syncEditorThemeToPortal } from "../utils/syncEditorThemeToPortal";

const props = defineProps<{
  modelValue: string;
  placeholder: string;
  inputId: string;
  clearLabel: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const { t } = useI18n();

const open = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const popoverRef = ref<HTMLElement | null>(null);

const viewYear = ref(new Date().getFullYear());
const viewMonth = ref(new Date().getMonth());

const { bottom, left, width, update } = useElementBounding(triggerRef);

const popoverStyle = computed(() => ({
  top: `${bottom.value + 4}px`,
  left: `${left.value}px`,
  width: `${Math.max(width.value, 268)}px`,
}));

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function toISO(y: number, monthIndex: number, day: number): string {
  return `${y}-${pad2(monthIndex + 1)}-${pad2(day)}`;
}

function parseISO(s: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) {
    return null;
  }
  return { y, m: mo, d };
}

function isoToday(): string {
  const n = new Date();
  return toISO(n.getFullYear(), n.getMonth(), n.getDate());
}

function buildCalendarCells(y: number, monthIndex: number) {
  type Cell = { iso: string; day: number; inMonth: boolean };
  const cells: Cell[] = [];
  const first = new Date(y, monthIndex, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(y, monthIndex + 1, 0).getDate();

  const prevLast = new Date(y, monthIndex, 0);
  const prevDays = prevLast.getDate();
  const prevM = monthIndex === 0 ? 11 : monthIndex - 1;
  const prevY = monthIndex === 0 ? y - 1 : y;

  for (let i = startPad - 1; i >= 0; i--) {
    const day = prevDays - i;
    cells.push({
      iso: toISO(prevY, prevM, day),
      day,
      inMonth: false,
    });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({
      iso: toISO(y, monthIndex, d),
      day: d,
      inMonth: true,
    });
  }

  const nextY = monthIndex === 11 ? y + 1 : y;
  const nextM = monthIndex === 11 ? 0 : monthIndex + 1;
  let nextD = 1;
  while (cells.length % 7 !== 0) {
    cells.push({
      iso: toISO(nextY, nextM, nextD),
      day: nextD++,
      inMonth: false,
    });
  }
  return cells;
}

const weekdayLabels = computed(() => {
  const labels: string[] = [];
  const sun = new Date(2024, 0, 7);
  for (let i = 0; i < 7; i++) {
    const d = new Date(sun);
    d.setDate(sun.getDate() + i);
    labels.push(
      new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(d),
    );
  }
  return labels;
});

const monthTitle = computed(() =>
  new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric",
  }).format(new Date(viewYear.value, viewMonth.value, 1)),
);

const cells = computed(() =>
  buildCalendarCells(viewYear.value, viewMonth.value),
);

const todayStr = computed(() => isoToday());

function formatDisplay(iso: string): string {
  const p = parseISO(iso);
  if (!p) return iso;
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(p.y, p.m, p.d));
}

function syncViewToModel(): void {
  const p = parseISO(props.modelValue);
  if (p) {
    viewYear.value = p.y;
    viewMonth.value = p.m;
  } else {
    const n = new Date();
    viewYear.value = n.getFullYear();
    viewMonth.value = n.getMonth();
  }
}

function toggleOpen(): void {
  open.value = !open.value;
}

watch(open, async (isOpen) => {
  if (!isOpen) return;
  syncViewToModel();
  await nextTick();
  await nextTick();
  update();
  syncEditorThemeToPortal(triggerRef.value, popoverRef.value);
});

onClickOutside(
  popoverRef,
  () => {
    open.value = false;
  },
  { ignore: [triggerRef] },
);

useEventListener(window, "keydown", (e: KeyboardEvent) => {
  if (e.key === "Escape") open.value = false;
});

useEventListener(window, "scroll", () => open.value && update(), {
  capture: true,
});

useEventListener(window, "resize", () => open.value && update());

function prevMonth(): void {
  if (viewMonth.value === 0) {
    viewMonth.value = 11;
    viewYear.value--;
  } else {
    viewMonth.value--;
  }
}

function nextMonth(): void {
  if (viewMonth.value === 11) {
    viewMonth.value = 0;
    viewYear.value++;
  } else {
    viewMonth.value++;
  }
}

function pick(iso: string): void {
  emit("update:modelValue", iso);
  open.value = false;
}

function clear(): void {
  emit("update:modelValue", "");
  open.value = false;
}

function pickToday(): void {
  pick(isoToday());
}
</script>

<template>
  <div
    class="tpl:flex tpl:items-center tpl:gap-0.5 tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:pr-1"
  >
    <button
      :id="inputId"
      ref="triggerRef"
      type="button"
      class="tpl:flex tpl:min-w-0 tpl:flex-1 tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:rounded-lg tpl:border-none tpl:bg-transparent tpl:px-3 tpl:py-2.5 tpl:text-left tpl:text-sm tpl:outline-none focus-visible:tpl:ring-2 focus-visible:tpl:ring-[var(--tpl-primary)] focus-visible:tpl:ring-offset-2 focus-visible:tpl:ring-offset-[var(--tpl-bg)]"
      :aria-expanded="open"
      aria-haspopup="dialog"
      @click="toggleOpen"
    >
      <span
        :class="
          modelValue
            ? 'tpl:text-[var(--tpl-text)]'
            : 'tpl:text-[var(--tpl-text-muted)]'
        "
      >
        {{ modelValue ? formatDisplay(modelValue) : placeholder }}
      </span>
      <CalendarDays
        :size="16"
        :stroke-width="1.5"
        class="tpl:ml-auto tpl:shrink-0 tpl:opacity-60"
        aria-hidden="true"
      />
    </button>
    <button
      v-if="modelValue"
      type="button"
      class="tpl:flex tpl:h-8 tpl:w-8 tpl:shrink-0 tpl:cursor-pointer tpl:items-center tpl:justify-center tpl:rounded-md tpl:border-none tpl:bg-transparent tpl:text-[var(--tpl-text-muted)] hover:tpl:bg-[var(--tpl-bg-hover)] hover:tpl:text-[var(--tpl-text)] focus-visible:tpl:outline focus-visible:tpl:outline-2 focus-visible:tpl:outline-[var(--tpl-primary)]"
      :aria-label="clearLabel"
      @click="clear"
    >
      ×
    </button>
  </div>

  <Teleport to="body">
    <div
      v-if="open"
      ref="popoverRef"
      class="tpl:fixed tpl:z-[100] tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:p-3 tpl:shadow-[var(--tpl-shadow-md)]"
      :style="popoverStyle"
      role="dialog"
      aria-modal="true"
      :aria-label="t.popupSchedule.calendarDialogLabel"
    >
      <div
        class="tpl:mb-3 tpl:flex tpl:items-center tpl:justify-between tpl:gap-2"
      >
        <button
          type="button"
          class="tpl:flex tpl:h-8 tpl:w-8 tpl:shrink-0 tpl:cursor-pointer tpl:items-center tpl:justify-center tpl:rounded-md tpl:border-none tpl:bg-transparent tpl:text-[var(--tpl-text-muted)] hover:tpl:bg-[var(--tpl-bg-hover)] hover:tpl:text-[var(--tpl-text)] focus-visible:tpl:outline focus-visible:tpl:outline-2 focus-visible:tpl:outline-[var(--tpl-primary)]"
          :aria-label="t.popupSchedule.calendarPrevMonth"
          @click="prevMonth"
        >
          <ChevronLeft :size="18" :stroke-width="1.75" aria-hidden="true" />
        </button>
        <span
          class="tpl:flex-1 tpl:text-center tpl:text-sm tpl:font-medium tpl:text-[var(--tpl-text)]"
        >
          {{ monthTitle }}
        </span>
        <button
          type="button"
          class="tpl:flex tpl:h-8 tpl:w-8 tpl:shrink-0 tpl:cursor-pointer tpl:items-center tpl:justify-center tpl:rounded-md tpl:border-none tpl:bg-transparent tpl:text-[var(--tpl-text-muted)] hover:tpl:bg-[var(--tpl-bg-hover)] hover:tpl:text-[var(--tpl-text)] focus-visible:tpl:outline focus-visible:tpl:outline-2 focus-visible:tpl:outline-[var(--tpl-primary)]"
          :aria-label="t.popupSchedule.calendarNextMonth"
          @click="nextMonth"
        >
          <ChevronRight :size="18" :stroke-width="1.75" aria-hidden="true" />
        </button>
      </div>

      <div
        class="tpl:grid tpl:grid-cols-7 tpl:gap-x-0 tpl:gap-y-1 tpl:text-[11px] tpl:font-medium tpl:uppercase tpl:tracking-wide tpl:text-[var(--tpl-text-muted)]"
      >
        <span
          v-for="(wd, i) in weekdayLabels"
          :key="`wd-${i}`"
          class="tpl:block tpl:text-center"
        >
          {{ wd }}
        </span>
      </div>

      <div class="tpl:mt-1 tpl:grid tpl:grid-cols-7 tpl:gap-y-1">
        <button
          v-for="(cell, i) in cells"
          :key="`${cell.iso}-${i}`"
          type="button"
          class="tpl:flex tpl:h-9 tpl:w-full tpl:cursor-pointer tpl:items-center tpl:justify-center tpl:rounded-md tpl:border-none tpl:bg-transparent tpl:text-sm tpl:outline-none focus-visible:tpl:ring-2 focus-visible:tpl:ring-[var(--tpl-primary)] focus-visible:tpl:ring-offset-2 focus-visible:tpl:ring-offset-[var(--tpl-bg)]"
          :class="[
            cell.inMonth
              ? 'tpl:text-[var(--tpl-text)]'
              : 'tpl:text-[var(--tpl-text-muted)] tpl:opacity-70',
            modelValue === cell.iso
              ? 'tpl:bg-[var(--tpl-primary)] tpl:font-medium tpl:text-white hover:tpl:bg-[var(--tpl-primary)]'
              : cell.iso === todayStr
                ? 'tpl:ring-1 tpl:ring-[var(--tpl-border)] tpl:ring-inset'
                : 'hover:tpl:bg-[var(--tpl-bg-hover)]',
          ]"
          @click="pick(cell.iso)"
        >
          {{ cell.day }}
        </button>
      </div>

      <div
        class="tpl:mt-3 tpl:flex tpl:flex-wrap tpl:items-center tpl:justify-between tpl:gap-2 tpl:border-t tpl:border-[var(--tpl-border)] tpl:pt-3"
      >
        <button
          type="button"
          class="tpl:rounded-md tpl:border-none tpl:bg-transparent tpl:px-2 tpl:py-1.5 tpl:text-xs tpl:font-medium tpl:text-[var(--tpl-primary)] tpl:underline tpl:underline-offset-2 hover:tpl:bg-[var(--tpl-bg-hover)] focus-visible:tpl:outline focus-visible:tpl:outline-2 focus-visible:tpl:outline-[var(--tpl-primary)]"
          @click="pickToday"
        >
          {{ t.popupSchedule.calendarToday }}
        </button>
        <button
          v-if="modelValue"
          type="button"
          class="tpl:rounded-md tpl:border-none tpl:bg-transparent tpl:px-2 tpl:py-1.5 tpl:text-xs tpl:font-medium tpl:text-[var(--tpl-text-muted)] hover:tpl:bg-[var(--tpl-bg-hover)] hover:tpl:text-[var(--tpl-text)] focus-visible:tpl:outline focus-visible:tpl:outline-2 focus-visible:tpl:outline-[var(--tpl-primary)]"
          @click="clear"
        >
          {{ t.popupSchedule.calendarClear }}
        </button>
      </div>
    </div>
  </Teleport>
</template>
